import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchSitePageSettings } from "../services/siteSettings";
import type { SitePageKey, SitePageToggleMap } from "../types/dataTypes";
import { SITE_PAGE_DEFAULTS } from "../data/sitePages";

interface PageSettingsContextValue {
  pageStates: SitePageToggleMap;
  loading: boolean;
  error: any | null;
  refresh: () => Promise<void>;
  setLocalState: (key: SitePageKey, enabled: boolean) => void;
  isPageEnabled: (key: SitePageKey) => boolean;
}

const defaultValue: PageSettingsContextValue = {
  pageStates: { ...SITE_PAGE_DEFAULTS },
  loading: true,
  error: null,
  refresh: async () => {},
  setLocalState: () => {},
  isPageEnabled: () => true,
};

const PageSettingsContext =
  createContext<PageSettingsContextValue>(defaultValue);

export function PageSettingsProvider({ children }: { children: ReactNode }) {
  const [pageStates, setPageStates] = useState<SitePageToggleMap>(() => ({
    ...SITE_PAGE_DEFAULTS,
  }));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await fetchSitePageSettings();
      setPageStates(settings);
      setError(null);
    } catch (err) {
      console.error("Failed to load page settings", err);
      setPageStates({ ...SITE_PAGE_DEFAULTS });
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setLocalState = useCallback((key: SitePageKey, enabled: boolean) => {
    setPageStates((prev) => ({ ...prev, [key]: enabled }));
  }, []);

  const isPageEnabled = useCallback(
    (key: SitePageKey) => pageStates[key] !== false,
    [pageStates],
  );

  const value = useMemo(
    () => ({
      pageStates,
      loading,
      error,
      refresh,
      setLocalState,
      isPageEnabled,
    }),
    [pageStates, loading, error, refresh, setLocalState, isPageEnabled],
  );

  return (
    <PageSettingsContext.Provider value={value}>
      {children}
    </PageSettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- expose hook alongside provider in same module for convenience
export function usePageSettings(): PageSettingsContextValue {
  const ctx = useContext(PageSettingsContext);
  if (!ctx) throw new Error("usePageSettings must be used within provider");
  return ctx;
}
